import { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Send,
  Flag,
  AlertCircle,
  Sparkles,
  Siren,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { getQuestionText, getChoiceText, getExplanation } from "@/lib/localize";
import { submitAnswer, completeTest, generateExplanation } from "@/lib/api";
import { renderQuestionHtml } from "@/lib/render-question-html";
import { isSafeImageUrl } from "@/lib/safe-url";
import { createErrorMessage, isApiErrorStatus } from "@/lib/error-message";
import type { Question, TestSession, UserAnswer } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Markdown } from "@/components/ui/markdown";
import { ReportDialog } from "@/components/report-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_main/exams/$slug/test/$id/")({
  component: TestPage,
});

// ─── Types ──────────────────────────────────────────────────────────────────

interface TestData {
  session: TestSession;
  questions: Question[];
}

interface AnswerState {
  selectedLabels: string[];
  submitted: boolean;
  result: UserAnswer | null;
  timeSpentMs: number;
  isAmbiguous?: boolean;
}

// ─── Storage helpers ────────────────────────────────────────────────────────

function loadTestData(testId: string): TestData | null {
  try {
    const raw = localStorage.getItem(`test_${testId}`);
    if (!raw) return null;
    return JSON.parse(raw) as TestData;
  } catch {
    return null;
  }
}

function saveAnswersState(
  testId: string,
  answers: Map<number, AnswerState>,
  currentIndex: number,
) {
  try {
    const serializable = Object.fromEntries(answers);
    localStorage.setItem(
      `test_answers_${testId}`,
      JSON.stringify({ answers: serializable, currentIndex }),
    );
  } catch {
    // Silently fail
  }
}

function loadAnswersState(
  testId: string,
): { answers: Map<number, AnswerState>; currentIndex: number } | null {
  try {
    const raw = localStorage.getItem(`test_answers_${testId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const map = new Map<number, AnswerState>(Object.entries(parsed.answers).map(
      ([k, v]) => [Number(k), v as AnswerState],
    ));
    return { answers: map, currentIndex: parsed.currentIndex };
  } catch {
    return null;
  }
}

// ─── Timer hook ─────────────────────────────────────────────────────────────

function useTimer(running: boolean) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!running) return;
    startRef.current = Date.now();
    setElapsed(0);

    const interval = setInterval(() => {
      setElapsed(Date.now() - startRef.current);
    }, 100);

    return () => clearInterval(interval);
  }, [running]);

  const reset = useCallback(() => {
    startRef.current = Date.now();
    setElapsed(0);
  }, []);

  return { elapsed, reset };
}

function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function TestPage() {
  const params = useParams({ strict: false }) as { slug: string; id: string };
  const examSlug = params.slug;
  const testId = params.id;
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();

  const [testData, setTestData] = useState<TestData | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<number, AnswerState>>(new Map());
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [accessRevoked, setAccessRevoked] = useState(false);

  const currentQuestion = testData?.questions[currentIndex] ?? null;
  const currentAnswer = currentQuestion
    ? answers.get(currentQuestion.id)
    : undefined;
  const isSubmitted = currentAnswer?.submitted ?? false;
  const isPractice = testData?.session.mode === "practice";

  // Timer: runs when current question is not submitted
  const { elapsed: timerElapsed, reset: resetTimer } = useTimer(
    loaded && !isSubmitted && !!currentQuestion,
  );

  // Load test data from localStorage
  useEffect(() => {
    const data = loadTestData(testId);
    if (!data) {
      setError("시험 데이터를 찾을 수 없습니다. 새 시험을 시작해 주세요.");
      setLoaded(true);
      return;
    }
    setTestData(data);

    // Restore answer state if available
    const savedState = loadAnswersState(testId);
    if (savedState) {
      setAnswers(savedState.answers);
      setCurrentIndex(savedState.currentIndex);
    }

    setLoaded(true);
  }, [testId]);

  // Persist answers to localStorage whenever they change
  useEffect(() => {
    if (testData && loaded) {
      saveAnswersState(testId, answers, currentIndex);
    }
  }, [answers, currentIndex, testData, testId, loaded]);

  // Reset timer when navigating to a new question
  useEffect(() => {
    if (!isSubmitted) {
      resetTimer();
    }
  }, [currentIndex, isSubmitted, resetTimer]);

  // Scroll to top whenever the question changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [currentIndex]);

  // ─── Handlers ───────────────────────────────────────────────────────────

  function toggleLabel(label: string) {
    if (!currentQuestion || isSubmitted) return;

    setAnswers((prev) => {
      const next = new Map(prev);
      const existing = next.get(currentQuestion.id);
      const currentLabels = existing?.selectedLabels ?? [];

      let newLabels: string[];
      if (currentQuestion.is_multiple) {
        if (currentLabels.includes(label)) {
          newLabels = currentLabels.filter((l) => l !== label);
        } else {
          newLabels = [...currentLabels, label];
        }
      } else {
        newLabels = [label];
      }

      next.set(currentQuestion.id, {
        selectedLabels: newLabels,
        submitted: false,
        result: null,
        timeSpentMs: 0,
      });
      return next;
    });
  }

  async function handleSubmitAnswer() {
    if (!currentQuestion || !testData || accessRevoked) return;
    const answer = answers.get(currentQuestion.id);
    if (!answer || answer.selectedLabels.length === 0) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await submitAnswer(examSlug, testId, {
        question_id: currentQuestion.id,
        selected_labels: answer.selectedLabels,
        time_spent_ms: timerElapsed,
      });

      setAnswers((prev) => {
        const next = new Map(prev);
        next.set(currentQuestion.id, {
          ...answer,
          submitted: true,
          result,
          timeSpentMs: timerElapsed,
        });
        return next;
      });
    } catch (err) {
      if (isApiErrorStatus(err, 403)) {
        handleExamAccessRevoked();
        return;
      }
      setError(createErrorMessage(err, "답안 제출 중 오류가 발생했습니다."));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCompleteTest() {
    if (accessRevoked) return;
    setCompleting(true);
    setError(null);

    try {
      await completeTest(examSlug, testId);
      // Commit dialog close before navigating so the portal isn't mid-exit
      // animation when the route unmounts (avoids insertBefore NotFoundError).
      flushSync(() => {
        setConfirmCompleteOpen(false);
      });
      navigate({
        to: "/exams/$slug/test/$id/result",
        params: { slug: examSlug, id: testId },
      });
    } catch (err) {
      if (isApiErrorStatus(err, 403)) {
        handleExamAccessRevoked();
        return;
      }
      setError(createErrorMessage(err, "시험 완료 처리 중 오류가 발생했습니다."));
      setCompleting(false);
    }
  }

  async function handleGenerateExplanation() {
    if (!currentQuestion) return;
    setGeneratingExplanation(true);
    try {
      const updated = await generateExplanation(examSlug, currentQuestion.id);
      if (updated.explanation && testData) {
        const newQuestions = testData.questions.map((q) =>
          q.id === currentQuestion.id ? { ...q, explanation: updated.explanation } : q,
        );
        setTestData({ ...testData, questions: newQuestions });
      }
    } catch {
      setError("해설 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setGeneratingExplanation(false);
    }
  }

  function toggleAmbiguous() {
    if (!currentQuestion || accessRevoked) return;
    setAnswers((prev) => {
      const next = new Map(prev);
      const existing = next.get(currentQuestion.id);
      next.set(currentQuestion.id, {
        selectedLabels: existing?.selectedLabels ?? [],
        submitted: existing?.submitted ?? false,
        result: existing?.result ?? null,
        timeSpentMs: existing?.timeSpentMs ?? 0,
        isAmbiguous: !(existing?.isAmbiguous ?? false),
      });
      return next;
    });
  }

  function goToQuestion(index: number) {
    if (!accessRevoked && index >= 0 && testData && index < testData.questions.length) {
      setCurrentIndex(index);
    }
  }

  function handleExamAccessRevoked() {
    setAccessRevoked(true);
    setConfirmCompleteOpen(false);
    setError("시험 접근 권한이 회수되어 더 이상 답안을 제출할 수 없습니다.");
    setCompleting(false);
  }

  // ─── Computed values ────────────────────────────────────────────────────

  const totalQuestions = testData?.questions.length ?? 0;
  const answeredCount = Array.from(answers.values()).filter(
    (a) => a.submitted,
  ).length;
  const progressPercent =
    totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
  const isLastQuestion = currentIndex === totalQuestions - 1;
  const allAnswered = answeredCount === totalQuestions;
  const ambiguousQuestions =
    testData?.questions.filter((q) => answers.get(q.id)?.isAmbiguous) ?? [];
  const unansweredQuestions =
    testData?.questions.filter((q) => !answers.get(q.id)?.submitted) ?? [];
  const selectedLabels = currentAnswer?.selectedLabels ?? [];

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!loaded || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!testData) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4">
        <AlertCircle className="h-12 w-12 text-zinc-400" />
        <p className="text-center text-zinc-600 dark:text-zinc-400">
          {error || (locale === "en" ? "Test data not found." : "시험 데이터를 찾을 수 없습니다.")}
        </p>
        <Button
          onClick={() =>
            navigate({
              to: "/exams/$slug/test/new",
              params: { slug: examSlug },
            })
          }
        >
          {t("result.new_test")}
        </Button>
      </div>
    );
  }

  if (accessRevoked) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-zinc-50 px-4 dark:bg-zinc-950">
        <div className="rounded-full bg-amber-100 p-4 dark:bg-amber-950">
          <AlertCircle className="h-10 w-10 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="max-w-md text-center">
          <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">
            시험 접근 권한이 없습니다
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
            시험 진행 중 권한이 변경되어 답안 제출과 완료 처리가 중단되었습니다.
            현재 선택한 답안은 이 브라우저에 임시 저장되어 있습니다.
          </p>
        </div>
        <div className="flex w-full max-w-md flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate({ to: "/dashboard" })}
          >
            대시보드로 이동
          </Button>
          <Button
            className="flex-1"
            onClick={() => navigate({ to: "/request-exam" })}
          >
            권한 요청하기
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-zinc-50 dark:bg-zinc-950">
      {/* Top bar: progress + timer */}
      <div className="sticky top-0 z-10 border-b border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900 sm:px-4 sm:py-3">
        <div className="mx-auto max-w-3xl">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={isPractice ? "default" : "secondary"} className="text-xs">
                {isPractice ? t("test.practice_mode") : t("test.exam_mode")}
              </Badge>
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                {currentIndex + 1} / {totalQuestions}
              </span>
            </div>
            <div className="flex items-center gap-1 text-sm text-zinc-500">
              <Clock className="h-4 w-4" />
              <span className="font-mono">{formatTime(timerElapsed)}</span>
            </div>
          </div>
          <Progress value={progressPercent} className="h-2" />
          <div className="mt-1 text-right text-xs text-zinc-400">
            {answeredCount}/{totalQuestions} {t("common.complete")}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto w-full max-w-3xl flex-1 px-3 py-4 sm:px-4 sm:py-6">
        {currentQuestion && (
          <div className="space-y-5 sm:space-y-6">
            {/* Question header */}
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">
                {t("common.question")} {currentQuestion.display_number ?? currentQuestion.question_number}
              </h2>
              {currentQuestion.is_multiple && (
                <Badge
                  variant="outline"
                  className="flex-shrink-0 text-xs text-orange-600 border-orange-300 dark:text-orange-400 dark:border-orange-700"
                >
                  {t("test.select_two")}
                </Badge>
              )}
            </div>

            {/* Question text - server-seeded content (admin-only input, not user-generated) */}
            <Card>
              <CardContent className="py-4">
                <div
                  className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800 dark:text-zinc-200 sm:text-base [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{
                    __html: renderQuestionHtml(getQuestionText(currentQuestion, locale)),
                  }}
                />
                {isSafeImageUrl(currentQuestion.image_url) && (
                  <div className="mt-3">
                    <img
                      src={currentQuestion.image_url}
                      alt={`${t("common.question")} ${currentQuestion.display_number ?? currentQuestion.question_number}`}
                      className="max-w-full rounded-lg border border-zinc-200 dark:border-zinc-700"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Choices */}
            <div className="space-y-3">
              {currentQuestion.choices?.map((choice) => {
                const isSelected = selectedLabels.includes(choice.label);
                const showResult = isSubmitted && isPractice;
                const isCorrect = choice.is_correct;

                let choiceStyle =
                  "border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900";

                if (showResult) {
                  if (isCorrect) {
                    choiceStyle =
                      "border-green-400 bg-green-50 dark:border-green-600 dark:bg-green-950";
                  } else if (isSelected && !isCorrect) {
                    choiceStyle =
                      "border-red-400 bg-red-50 dark:border-red-600 dark:bg-red-950";
                  }
                } else if (isSelected) {
                  choiceStyle =
                    "border-blue-400 bg-blue-50 ring-2 ring-blue-200 dark:border-blue-500 dark:bg-blue-950 dark:ring-blue-800";
                }

                return (
                  <button
                    key={choice.label}
                    type="button"
                    disabled={isSubmitted}
                    onClick={() => toggleLabel(choice.label)}
                    className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all min-h-[48px] ${choiceStyle} ${
                      isSubmitted
                        ? "cursor-default"
                        : "cursor-pointer hover:border-blue-300 active:scale-[0.99] dark:hover:border-blue-600"
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                        showResult && isCorrect
                          ? "bg-green-500 text-white"
                          : showResult && isSelected && !isCorrect
                            ? "bg-red-500 text-white"
                            : isSelected
                              ? "bg-blue-500 text-white"
                              : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
                      }`}
                    >
                      {choice.label}
                    </span>

                    <span className="flex-1 pt-1 text-sm text-zinc-700 dark:text-zinc-300 sm:text-base">
                      {getChoiceText(choice, locale)}
                    </span>

                    {showResult && isCorrect && (
                      <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-green-500" />
                    )}
                    {showResult && isSelected && !isCorrect && (
                      <XCircle className="mt-1 h-5 w-5 flex-shrink-0 text-red-500" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Result feedback */}
            {isSubmitted && isPractice && currentAnswer?.result && (
              <Card
                className={`border-2 ${
                  currentAnswer.result.is_correct
                    ? "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950"
                    : "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950"
                }`}
              >
                <CardContent className="flex items-center gap-3 py-4">
                  {currentAnswer.result.is_correct ? (
                    <>
                      <CheckCircle2 className="h-6 w-6 flex-shrink-0 text-green-500" />
                      <div>
                        <p className="font-semibold text-green-700 dark:text-green-300">
                          {t("test.correct_answer")}
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          {t("test.time_spent")}: {formatTime(currentAnswer.timeSpentMs)}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-6 w-6 flex-shrink-0 text-red-500" />
                      <div>
                        <p className="font-semibold text-red-700 dark:text-red-300">
                          {t("test.incorrect_answer")}
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {t("test.incorrect_desc")}: {formatTime(currentAnswer.timeSpentMs)}
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {isSubmitted && !isPractice && (
              <Card className="border-2 border-zinc-300 bg-zinc-100 dark:border-zinc-600 dark:bg-zinc-900">
                <CardContent className="flex items-center gap-3 py-4">
                  <CheckCircle2 className="h-6 w-6 flex-shrink-0 text-zinc-500" />
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                    {t("test.exam_mode_submitted")}
                  </p>
                </CardContent>
              </Card>
            )}

            {isSubmitted && isPractice && (
              getExplanation(currentQuestion, locale) ? (
                <Card className="border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
                  <CardContent className="py-4">
                    <p className="mb-1 text-xs font-semibold text-blue-600 dark:text-blue-400">
                      {t("common.explanation")}
                    </p>
                    <div className="text-sm leading-relaxed text-blue-800 dark:text-blue-200">
                      <Markdown>{getExplanation(currentQuestion, locale)!}</Markdown>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Button
                  variant="outline"
                  className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950"
                  onClick={handleGenerateExplanation}
                  disabled={generatingExplanation}
                >
                  {generatingExplanation ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("questions.ai_explanation_loading")}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t("questions.ai_explanation")}
                    </>
                  )}
                </Button>
              )
            )}

            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Complete test button at the bottom of the question area */}
            {(isLastQuestion ? isSubmitted || !isPractice : allAnswered) && (
              <Button
                className="h-12 w-full min-h-[48px] bg-green-600 hover:bg-green-700 text-white text-base font-semibold"
                disabled={completing || (!allAnswered && isPractice)}
                onClick={() => {
                  setError(null);
                  setConfirmCompleteOpen(true);
                }}
              >
                {completing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t("common.processing")}
                  </>
                ) : (
                  <>
                    <Flag className="mr-2 h-5 w-5" />
                    {t("test.complete_test")}
                    {!isLastQuestion && ` (${answeredCount}/${totalQuestions})`}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Report Dialog */}
      {currentQuestion && (
        <ReportDialog
          examSlug={examSlug}
          questionNumber={currentQuestion.question_number}
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
        />
      )}

      {/* Confirm Complete Dialog */}
      <Dialog
        open={confirmCompleteOpen}
        onOpenChange={(open) => {
          if (completing) return;
          setConfirmCompleteOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("test.confirm_complete_title")}</DialogTitle>
            <DialogDescription>
              {t("test.confirm_complete_desc")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex max-h-[50vh] flex-col gap-3 overflow-y-auto">
            {unansweredQuestions.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
                <p className="text-xs font-semibold text-red-700 dark:text-red-300">
                  {t("test.unanswered_questions")} · {unansweredQuestions.length}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {unansweredQuestions.map((q) => {
                    const idx = testData.questions.findIndex((it) => it.id === q.id);
                    return (
                      <button
                        key={q.id}
                        type="button"
                        aria-label={t("test.review_question")}
                        onClick={() => {
                          if (idx >= 0) goToQuestion(idx);
                          setConfirmCompleteOpen(false);
                        }}
                        className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border border-red-300 bg-white px-2 text-xs font-medium text-red-700 hover:bg-red-100 dark:border-red-700 dark:bg-red-950 dark:text-red-200 dark:hover:bg-red-900"
                      >
                        {q.display_number ?? q.question_number}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {ambiguousQuestions.length > 0 ? (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
                <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-300">
                  {t("test.unsure_questions")} · {ambiguousQuestions.length}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ambiguousQuestions.map((q) => {
                    const idx = testData.questions.findIndex((it) => it.id === q.id);
                    return (
                      <button
                        key={q.id}
                        type="button"
                        aria-label={t("test.review_question")}
                        onClick={() => {
                          if (idx >= 0) goToQuestion(idx);
                          setConfirmCompleteOpen(false);
                        }}
                        className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border border-yellow-400 bg-white px-2 text-xs font-medium text-yellow-800 hover:bg-yellow-100 dark:border-yellow-600 dark:bg-yellow-950 dark:text-yellow-200 dark:hover:bg-yellow-900"
                      >
                        {q.display_number ?? q.question_number}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("test.no_unsure")}
              </p>
            )}

            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmCompleteOpen(false)}
              disabled={completing}
            >
              {t("common.cancel")}
            </Button>
            <Button
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={handleCompleteTest}
              disabled={completing}
            >
              {completing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.processing")}
                </>
              ) : (
                <>
                  <Flag className="mr-2 h-4 w-4" />
                  {t("test.confirm_complete_submit")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom navigation */}
      <div className="sticky bottom-0 z-30 border-t border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <div className="mx-auto max-w-3xl px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:px-4 sm:pt-3 sm:pb-3">
          <div className="mb-2 flex flex-wrap justify-center gap-1 sm:mb-3">
            {testData.questions.map((q, idx) => {
              const ans = answers.get(q.id);
              const isCurrent = idx === currentIndex;
              const isAnswered = ans?.submitted ?? false;
              const isMarkedAmbiguous = ans?.isAmbiguous ?? false;

              let dotStyle = "bg-zinc-200 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400";
              if (isCurrent) {
                dotStyle = "bg-blue-500 text-white ring-2 ring-blue-300 dark:ring-blue-700";
              } else if (isMarkedAmbiguous) {
                dotStyle = "bg-yellow-400 text-zinc-900 dark:bg-yellow-500 dark:text-zinc-900";
              } else if (isAnswered && isPractice && ans?.result) {
                dotStyle = ans.result.is_correct
                  ? "bg-green-500 text-white"
                  : "bg-red-500 text-white";
              } else if (isAnswered) {
                dotStyle = "bg-zinc-500 text-white";
              }

              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => goToQuestion(idx)}
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-medium transition-all sm:h-7 sm:w-7 sm:text-xs ${dotStyle}`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-10 min-h-[40px] flex-1 px-2 sm:h-11 sm:min-h-[44px]"
              disabled={currentIndex <= 0}
              onClick={() => goToQuestion(currentIndex - 1)}
            >
              <ChevronLeft className="h-5 w-5 sm:mr-1" />
              <span className="hidden sm:inline">{t("common.previous")}</span>
            </Button>

            <button
              type="button"
              onClick={toggleAmbiguous}
              aria-label={t("test.unsure")}
              aria-pressed={currentAnswer?.isAmbiguous ?? false}
              className={`flex h-10 min-h-[40px] flex-shrink-0 items-center justify-center gap-1 rounded-md border px-3 text-xs font-medium transition-colors sm:h-11 sm:min-h-[44px] ${
                currentAnswer?.isAmbiguous
                  ? "border-yellow-400 bg-yellow-100 text-yellow-800 dark:border-yellow-500 dark:bg-yellow-900 dark:text-yellow-200"
                  : "border-zinc-200 text-zinc-500 hover:border-yellow-300 hover:text-yellow-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-yellow-700 dark:hover:text-yellow-400"
              }`}
            >
              <Flag className="h-4 w-4" />
              <span className="hidden sm:inline">{t("test.unsure")}</span>
            </button>

            <button
              type="button"
              onClick={() => setReportDialogOpen(true)}
              aria-label={t("report.button")}
              className="flex h-10 min-h-[40px] flex-shrink-0 items-center justify-center gap-1 rounded-md border border-zinc-200 px-3 text-xs text-zinc-500 transition-colors hover:border-red-300 hover:text-red-500 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-red-700 sm:h-11 sm:min-h-[44px]"
            >
              <Siren className="h-4 w-4" />
              <span className="hidden sm:inline">{t("report.button")}</span>
            </button>

            <Button
              variant="outline"
              size="sm"
              className="h-10 min-h-[40px] flex-1 px-2 sm:h-11 sm:min-h-[44px]"
              disabled={currentIndex >= totalQuestions - 1}
              onClick={() => goToQuestion(currentIndex + 1)}
            >
              <span className="hidden sm:inline">{t("common.next")}</span>
              <ChevronRight className="h-5 w-5 sm:ml-1" />
            </Button>
          </div>

          {!isSubmitted && (
            <div className="mt-2 sm:mt-3">
              <Button
                className="h-12 w-full min-h-[48px] text-base font-semibold"
                disabled={selectedLabels.length === 0 || submitting}
                onClick={handleSubmitAnswer}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t("test.submitting")}
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    {t("test.submit_answer")}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
